import React from "react";
import { Upload } from "antd";
import { Button, Box, Typography } from "@material-ui/core";
import {
  UploadFolderProcessingIcon,
  IconUpload,
  DV3DocumentDownloadIcon,
  CloudGradientIcon,
} from "../../../../common/icons";
import { withStyles } from "@material-ui/core/styles";
import { singleRegistryTheme } from "../../theme";
import { uploadStatusEnum } from "../../enums/status-upload";
import TemplateDownloadModal from "../../modals/TemplateDownloadModal/TemplateDownloadModal";
import { injectIntl, defineMessages } from "react-intl";
import HUBContext from "../../../../services/HUBContext";
import {
  acceptedExtensionsExt,
  CustomLinearProgress,
  translations,
} from "./constants";
import { stylesFixed, stylesTheme } from "./UploadFile.styles";

const styles = (theme) => stylesTheme;

const messages = defineMessages(translations);

export class UploadFileBase extends React.Component {
  state = {
    selectedFile: null,
    isTemplateModalOpen: false,
  };

  draggerConfig = {
    openFileDialogOnClick: false,
    name: "file",
    multiple: false,
    showUploadList: false,
    beforeUpload: () => false,
    accept: acceptedExtensionsExt.join(","),
    onChange: (info) => {
      if (this.props.onFileChange) {
        this.setState({ selectedFile: info.file });
        this.props.onFileChange(info.file);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  inputRef = React.createRef();
  fileInputRef = React.createRef();

  componentDidUpdate(prevProps) {
    if (!prevProps.hasErrorUpload && this.props.hasErrorUpload) {
      this.setState({ selectedFile: null });
    }
  }

  downloadEmptyFile() {
    this.props.getEmptyFileVehicles();
  }

  handleHiddenInputChange = (e) => {
    const file = e.target.files && e.target.files[0];

    if (file) {
      this.setState({ selectedFile: file });
      this.props.onFileChange && this.props.onFileChange(file);
      e.target.value = null;
    }
  };

  handleOpenFileDialog = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleOpenTemplateModal = () => {
    this.setState({ isTemplateModalOpen: true });
  };

  handleCloseTemplateModal = () => {
    this.setState({ isTemplateModalOpen: false });
  };
}

export class UploadFile extends UploadFileBase {
  render() {
    const {
      hasErrorUpload,
      uploadErrorMessage,
      percent,
      status,
      companyId,
      intl,
    } = this.props;

    const isUploadingInProgress =
      status === uploadStatusEnum.SENDING ||
      status === uploadStatusEnum.COMPLETED;

    return (
      <Box>
        <input
          type="file"
          ref={this.fileInputRef}
          accept={acceptedExtensionsExt.join(",")}
          style={{ display: "none" }}
          onChange={this.handleHiddenInputChange}
        />

        <React.Fragment>
          <Box style={stylesFixed.boxHeader}>
            <Typography variant="h4" style={stylesFixed.headerTitle}>
              {intl.formatMessage(messages.title)}
            </Typography>

            <Typography style={stylesFixed.headerSubtitle}>
              {intl.formatMessage(messages.subtitle)}
            </Typography>
          </Box>

          <Upload.Dragger
            ref={this.inputRef}
            {...this.draggerConfig}
            style={stylesFixed.uploadDragger}
          >
            <div className="wrapper-upload" style={stylesFixed.wrapperUpload}>
              <Box sx={stylesFixed.boxUploadProgress}>
                {isUploadingInProgress ? (
                  <CloudGradientIcon />
                ) : (
                  <UploadFolderProcessingIcon />
                )}
              </Box>

              {hasErrorUpload ? (
                <React.Fragment>
                  {uploadErrorMessage.split("\n")?.map((message, key) => (
                    <Typography
                      key={key}
                      style={stylesFixed.errorMessage}
                      data-testid="upload-backend-error"
                    >
                      {message}
                    </Typography>
                  ))}
                </React.Fragment>
              ) : null}

              <p style={stylesFixed.draggerTitle}>
                {intl.formatMessage(messages.draggerTitle)}
              </p>

              <Box style={stylesFixed.selectedFileName}>
                {this.state.selectedFile && this.state.selectedFile?.name
                  ? this.state.selectedFile?.name
                  : null}
              </Box>

              {typeof percent === "number" && isUploadingInProgress && (
                <Box style={stylesFixed.percentProgress}>
                  <CustomLinearProgress variant="determinate" value={percent} />

                  <span>{percent}%</span>
                </Box>
              )}

              {!isUploadingInProgress && (
                <p style={stylesFixed.acceptedFiles}>
                  {intl.formatMessage(messages.acceptedFiles)}
                </p>
              )}

              <Box>
                {this.state.selectedFile && this.state.selectedFile?.name ? (
                  <Button
                    style={stylesFixed.replaceFileButton}
                    className="v3-button"
                    onClick={this.handleOpenFileDialog}
                    color="primary"
                  >
                    <div style={stylesFixed.replaceFileContent}>
                      <IconUpload fillcolor="#1E293B" />

                      <span style={stylesFixed.replaceFileInfo}>
                        {intl.formatMessage(messages.replaceFile)}
                      </span>
                    </div>
                  </Button>
                ) : (
                  <Button
                    style={stylesFixed.importFileButton}
                    className="v3-button black"
                    onClick={this.handleOpenFileDialog}
                    color="primary"
                  >
                    <div style={stylesFixed.importFileContent}>
                      <IconUpload fillcolor="#1E293B" />

                      <span style={stylesFixed.importFileInfo}>
                        {intl.formatMessage(messages.importFile)}
                      </span>
                    </div>
                  </Button>
                )}
              </Box>

              <Box
                onClick={this.handleOpenTemplateModal}
                sx={stylesFixed.downloadTemplateBox}
                tabIndex={0}
                role="button"
              >
                {intl.formatMessage(messages.downloadTemplate)}

                <DV3DocumentDownloadIcon color="#F72717" />
              </Box>

              <TemplateDownloadModal
                locale={this.context.locale}
                open={this.state.isTemplateModalOpen}
                onClose={this.handleCloseTemplateModal}
                companyId={companyId}
                integratorsSolutionsCode={this.props.integratorsSolutionsCode}
              />
            </div>
          </Upload.Dragger>
        </React.Fragment>
      </Box>
    );
  }
}

UploadFile.contextType = HUBContext;

export default withStyles(styles, { defaultTheme: singleRegistryTheme })(
  injectIntl(UploadFile),
);
